/**
 * Coerce API message fields into a non-empty string.
 * Nest may return string, string[], or nested error objects.
 */
function toSafeMessage(value: unknown): string | undefined {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || undefined;
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => (typeof item === 'string' ? item.trim() : ''))
      .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : undefined;
  }
  return undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

const DEFAULT_ERROR_MESSAGE = 'An unexpected error occurred. Please try again.';

/** Nest HTTP labels that are not actionable studio copy (ENR-1947). */
const GENERIC_NEST_LABELS = new Set([
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'conflict',
  'internal server error',
]);

function isGenericNestLabel(value: string | undefined): boolean {
  return !!value && GENERIC_NEST_LABELS.has(value.toLowerCase());
}

export const MAKEUP_CAPACITY_FULL_CODE = 'MAKEUP_CAPACITY_FULL';

/** True when the API rejected a makeup book because the class is at capacity. */
export function isMakeupCapacityFullError(error: unknown): boolean {
  try {
    if (!error || typeof error !== 'object' || !('response' in error)) {
      return false;
    }
    const response = (error as { response?: { data?: unknown; status?: number } })
      .response;
    if (response?.status !== 409) return false;
    const data = response.data;
    if (!isRecord(data)) return false;

    // The global exception filter nests the handler payload under `error`,
    // so check both the flat and nested shapes.
    const nested = isRecord(data.error) ? data.error : undefined;

    if (
      data.code === MAKEUP_CAPACITY_FULL_CODE ||
      nested?.code === MAKEUP_CAPACITY_FULL_CODE
    ) {
      return true;
    }

    const message = toSafeMessage(data.message) ?? toSafeMessage(nested?.message);
    return !!message && /full for this date/i.test(message);
  } catch {
    return false;
  }
}

/**
 * How the API classified a failed money-movement request. Server errors mean our own
 * code failed and no payment outcome can be inferred, so staff must not be told the
 * payment was declined.
 */
export type PaymentErrorCategory =
  | 'payment_declined'
  | 'payment_processor_error'
  | 'server_error';

const PAYMENT_ERROR_CATEGORIES: PaymentErrorCategory[] = [
  'payment_declined',
  'payment_processor_error',
  'server_error',
];

function toPaymentErrorCategory(
  value: unknown,
): PaymentErrorCategory | undefined {
  return typeof value === 'string' &&
    (PAYMENT_ERROR_CATEGORIES as string[]).includes(value)
    ? (value as PaymentErrorCategory)
    : undefined;
}

/**
 * Reads the `errorCategory` the API attaches to payment failures. The global exception
 * filter nests the handler payload under `error`, so check both shapes.
 */
export function getPaymentErrorCategory(
  error: unknown,
): PaymentErrorCategory | undefined {
  try {
    if (!error || typeof error !== 'object' || !('response' in error)) {
      return undefined;
    }
    const responseData = (error as { response?: { data?: unknown } }).response
      ?.data;
    if (!isRecord(responseData)) return undefined;

    return (
      toPaymentErrorCategory(responseData.errorCategory) ??
      (isRecord(responseData.error)
        ? toPaymentErrorCategory(responseData.error.errorCategory)
        : undefined)
    );
  } catch {
    return undefined;
  }
}

/**
 * Extracts a user-friendly error message from an API error response.
 * Always returns a non-empty string. Safe for null/undefined/empty errors.
 */
export function getErrorMessage(error: unknown, fallback?: string): string {
  const fallbackMessage =
    typeof fallback === 'string' && fallback.trim()
      ? fallback.trim()
      : DEFAULT_ERROR_MESSAGE;

  try {
    if (error == null) {
      return fallbackMessage;
    }

    // Handle Axios-style errors (response may be missing on network failures)
    if (typeof error === 'object' && error !== null && 'response' in error) {
      const axiosError = error as {
        response?: {
          data?: unknown;
          status?: number;
          statusText?: string;
        } | null;
        message?: unknown;
      };

      const response = axiosError.response;
      const responseData = response?.data;
      const data = isRecord(responseData) ? responseData : undefined;

      const nestedError = isRecord(data?.error) ? data.error : undefined;
      const nestedMessage = toSafeMessage(nestedError?.message);
      const topMessage = toSafeMessage(data?.message);

      // CustomExceptionFilter nests the handler payload under `error`. Prefer that
      // over a generic Nest label (e.g. "Bad Request") so staff see the backend text.
      if (nestedMessage && !isGenericNestLabel(nestedMessage)) {
        return nestedMessage;
      }

      if (data?.matchType === 'hard' && topMessage) {
        return topMessage;
      }
      if (topMessage && !isGenericNestLabel(topMessage)) {
        return topMessage;
      }

      if (typeof data?.error === 'string') {
        const errorString = toSafeMessage(data.error);
        if (errorString && !isGenericNestLabel(errorString)) return errorString;
      }

      // HTTP status-based messages when body had no usable message
      const status = response?.status;
      if (status === 400) {
        return 'Invalid request. Please check your input and try again.';
      }
      if (status === 401) {
        return 'You are not authorized to perform this action.';
      }
      if (status === 403) {
        return 'You do not have permission to perform this action.';
      }
      if (status === 404) {
        return 'The requested resource was not found.';
      }
      if (status === 409) {
        return 'A conflict occurred. This may be a duplicate entry.';
      }
      if (status === 422) {
        return 'Validation error. Please check your input.';
      }
      if (status === 500) {
        return 'Server error. Please try again later.';
      }
      if (typeof status === 'number' && status > 0) {
        return `Request failed with status ${status}. Please try again.`;
      }

      // Network / cancelled request with an Axios message
      const axiosMessage = toSafeMessage(axiosError.message);
      if (axiosMessage) return axiosMessage;
    }

    // Handle Error objects
    if (error instanceof Error) {
      return toSafeMessage(error.message) || fallbackMessage;
    }

    // Handle string errors
    if (typeof error === 'string') {
      return toSafeMessage(error) || fallbackMessage;
    }

    // Empty object / unrecognized shape
    return fallbackMessage;
  } catch {
    return fallbackMessage;
  }
}
